import type {
	LiveRateTableBlockElement,
	LiveRateTableSnapshot,
} from "../types";

/** Single tax-year label for public/runtime render (not the authoring chip switcher). */
export function resolveLiveRateTablePublicYearLabel(input: {
	tableSnapshot?: Pick<LiveRateTableSnapshot, "taxYear"> | null;
	yearChips?: readonly string[];
}): string | null {
	const taxYear = input.tableSnapshot?.taxYear?.trim();
	if (taxYear) {
		return taxYear;
	}
	const chips = (input.yearChips ?? []).filter(
		(chip): chip is string => typeof chip === "string" && chip.trim().length > 0
	);
	return chips.length === 1 ? chips[0].trim() : null;
}

export function liveRateTableSnapshotPatch(
	snapshot: LiveRateTableSnapshot,
	live: Pick<LiveRateTableBlockElement, "yearChips">
): Partial<LiveRateTableBlockElement> {
	const registryYearLabels =
		snapshot.yearLabels && snapshot.yearLabels.length > 0
			? snapshot.yearLabels
			: snapshot.taxYear
				? [snapshot.taxYear]
				: [];
	const existing = live.yearChips ?? [];
	const yearChips =
		existing.length > 0
			? mergeYearChipLabels(existing, registryYearLabels)
			: registryYearLabels;
	return {
		tableSnapshot: snapshot,
		yearChips,
	};
}

function mergeYearChipLabels(
	existing: readonly string[],
	registry: readonly string[]
): string[] {
	if (registry.length === 0) {
		return [...existing];
	}
	const seen = new Set<string>();
	const merged: string[] = [];
	for (const label of [...registry, ...existing]) {
		const trimmed = label.trim();
		if (!trimmed || seen.has(trimmed)) {
			continue;
		}
		seen.add(trimmed);
		merged.push(trimmed);
	}
	return merged.sort((left, right) => right.localeCompare(left));
}

/** Fetch registry table and build element patch (snapshot cache + year chips). */
export async function fetchLiveRateTablePatch(
	getResolvedRateTable: (
		rateSetKey: string,
		versionId?: string
	) => Promise<LiveRateTableSnapshot | null>,
	live: Pick<
		LiveRateTableBlockElement,
		"rateSetKey" | "selectedVersionId" | "yearChips"
	>
): Promise<Partial<LiveRateTableBlockElement> | null> {
	const key = live.rateSetKey?.trim();
	if (!key) {
		return null;
	}
	const snapshot = await getResolvedRateTable(
		key,
		live.selectedVersionId?.trim() || undefined
	);
	if (!snapshot) {
		return null;
	}
	return liveRateTableSnapshotPatch(snapshot, live);
}

export function snapshotsEqual(
	a: LiveRateTableSnapshot | null | undefined,
	b: LiveRateTableSnapshot | null | undefined
): boolean {
	if (!(a && b)) {
		return a === b;
	}
	if (a.versionId !== b.versionId || a.taxYear !== b.taxYear) {
		return false;
	}
	if ((a.yearLabels ?? []).join("|") !== (b.yearLabels ?? []).join("|")) {
		return false;
	}
	if (
		a.columns.length !== b.columns.length ||
		a.bands.length !== b.bands.length
	) {
		return false;
	}
	for (let i = 0; i < a.columns.length; i++) {
		const colA = a.columns[i];
		const colB = b.columns[i];
		if (colA.key !== colB.key || colA.name !== colB.name) {
			return false;
		}
	}
	for (let i = 0; i < a.bands.length; i++) {
		const rowA = a.bands[i];
		const rowB = b.bands[i];
		if (rowA.rowId !== rowB.rowId || rowA.label !== rowB.label) {
			return false;
		}
	}
	return true;
}
